"use client";

import { CloudUpload, X, File as FileIcon } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { toast } from "@/app/components/ui/sonner";
import {
  getJobContactFormDocumentTypeErrorMessage,
  JOB_CONTACT_FORM_ALLOWED_DOCUMENT_TYPES_LABEL,
} from "@/app/config/contact-form-file-limits";
import { cn } from "@/lib/utils";
import {
  useController,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

const formatMaxFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes >= 1024 * 1024) {
    return `${Math.round(sizeInBytes / 1024 / 1024)} MB`;
  }

  return `${Math.max(1, Math.round(sizeInBytes / 1024))} KB`;
};

const formatAcceptedFileTypes = (accept?: string): string | null => {
  if (!accept) {
    return null;
  }

  if (
    accept.includes(".pdf") &&
    accept.includes(".doc") &&
    accept.includes(".docx") &&
    accept.includes(".jpg") &&
    accept.includes(".jpeg") &&
    accept.includes(".png") &&
    accept.includes(".webp")
  ) {
    return JOB_CONTACT_FORM_ALLOWED_DOCUMENT_TYPES_LABEL;
  }

  const labels = accept
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
    .map((value) => {
      switch (value) {
        case ".pdf":
        case "application/pdf":
          return "PDF";
        case ".doc":
        case "application/msword":
          return "DOC";
        case ".docx":
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          return "DOCX";
        case ".jpg":
        case ".jpeg":
        case "image/jpeg":
          return "JPG oder JPEG";
        case ".png":
        case "image/png":
          return "PNG";
        case ".webp":
        case "image/webp":
          return "WEBP";
        case "image/*":
          return "Bilddateien";
        default:
          return null;
      }
    })
    .filter((value) => value !== null);

  const uniqueLabels = Array.from(new Set(labels));

  if (uniqueLabels.length === 0) {
    return null;
  }

  if (uniqueLabels.length === 1) {
    return uniqueLabels[0];
  }

  return `${uniqueLabels.slice(0, -1).join(", ")} oder ${uniqueLabels.at(-1)}`;
};

type ControlledFileInputProps<TFieldValues extends FieldValues = FieldValues> =
  {
    name: FieldPath<TFieldValues>;
    control?: Control<TFieldValues>;
    label?: string;
    buttonLabel?: string;
    accept?: string;
    maxSize?: number;
    required?: boolean;
    className?: string;
  };

/**
 * ControlledFileInput - React Hook Form integrated file upload component
 *
 * Displays a file upload button with cloud icon, similar to the design shown in the screenshot.
 * Stores the File object in the form state for later upload during submit.
 */
export function ControlledFileInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  label,
  buttonLabel,
  accept,
  maxSize,
  required = false,
  className,
}: ControlledFileInputProps<TFieldValues>) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "ControlledFileInput must be used inside FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: actualControl,
    rules: {
      required: required ? "Datei ist erforderlich" : false,
      validate: (value: File | undefined) => {
        if (!value) {
          return required ? "Datei ist erforderlich" : true;
        }

        if (maxSize && value.size > maxSize) {
          return `Datei ist zu groß. Erlaubt sind maximal ${formatMaxFileSize(maxSize)}.`;
        }

        return true;
      },
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const acceptedFileTypesLabel = formatAcceptedFileTypes(accept);
  const [fileName, setFileName] = useState<string | null>(() => {
    const value = field.value as File | string | undefined;
    if (value instanceof File) {
      return value.name;
    }
    if (typeof value === "string") {
      // If it's already a URL, extract filename from URL or show generic name
      return value.split("/").pop() || "Abrufschein";
    }
    return null;
  });

  // Update fileName when field value changes
  useEffect(() => {
    const value = field.value as File | string | undefined;
    if (value instanceof File) {
      setFileName(value.name);
    } else if (typeof value === "string" && value) {
      setFileName(value.split("/").pop() || "Abrufschein");
    } else {
      setFileName(null);
    }
  }, [field.value]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (maxSize && file.size > maxSize) {
        const maxSizeLabel = formatMaxFileSize(maxSize);

        field.onChange(file);
        formContext?.setError(name, {
          type: "manual",
          message: `Datei ist zu groß. Erlaubt sind maximal ${maxSizeLabel}.`,
        });
        setFileName(file.name);
        event.target.value = "";
        toast.error("Datei zu groß", {
          description: `Die Datei ${file.name} überschreitet die maximale Größe von ${maxSizeLabel}.`,
        });
        return;
      }

      // Validate file type if accept is specified
      if (accept) {
        const acceptedTypes = accept.split(",").map((type) => type.trim());
        const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
        const fileType = file.type;

        const isAccepted =
          acceptedTypes.some((type) => {
            if (type.startsWith(".")) {
              return fileExtension === type.toLowerCase();
            }
            if (type.includes("*")) {
              const baseType = type.split("/")[0];
              return fileType.startsWith(baseType);
            }
            return fileType === type;
          }) || fileType === accept;

        if (!isAccepted) {
          const errorMessage = acceptedFileTypesLabel
            ? acceptedFileTypesLabel ===
              JOB_CONTACT_FORM_ALLOWED_DOCUMENT_TYPES_LABEL
              ? getJobContactFormDocumentTypeErrorMessage(file.name)
              : `Die Datei ${file.name} kann nicht hochgeladen werden. Erlaubt sind ${acceptedFileTypesLabel}.`
            : `Die Datei ${file.name} kann nicht hochgeladen werden, weil das Format nicht unterstützt wird.`;

          formContext?.setError(name, {
            type: "manual",
            message: errorMessage,
          });
          event.target.value = "";
          toast.error("Dateiformat nicht unterstützt", {
            description: errorMessage,
          });
          return;
        }
      }

      formContext?.clearErrors(name);
      field.onChange(file);
      setFileName(file.name);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    field.onChange(undefined);
    formContext?.clearErrors(name);
    setFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = () => {
    field.onChange(undefined);
    formContext?.clearErrors(name);
    setFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && (
        <label className="text-md text-foreground font-semibold">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />

      <button
        type="button"
        onClick={handleButtonClick}
        className={cn(
          "border-border bg-background flex items-center justify-center gap-3 rounded-md border px-4 py-3 transition-all",
          "hover:bg-muted cursor-pointer active:scale-98",
          error && "border-destructive"
        )}
      >
        <div className="flex h-6 w-6 items-center justify-center">
          {fileName ? (
            <FileIcon className="text-foreground h-5 w-5" />
          ) : (
            <CloudUpload className="text-foreground h-5 w-5" />
          )}
        </div>
        <span className="text-foreground text-sm font-medium">
          {fileName || buttonLabel || "Datei auswählen"}
        </span>
        {fileName && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleRemove(e);
            }}
            className="text-muted-foreground hover:text-foreground flex h-5 w-5 cursor-pointer items-center justify-center transition-colors"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveFile();
              }
            }}
            aria-label="Datei entfernen"
          >
            <X className="h-4 w-4" />
          </div>
        )}
      </button>

      {error && <p className="text-destructive text-xs">{error.message}</p>}
    </div>
  );
}
